/*
  Patio · comportamiento común del tema (assets/patio.js, lo define la Base).
  Sale del prototipo «Muro» (paginas/patio.js y vanos.html): menú de celular, cabecera que toma el color
  del muro, favoritos guardados en este navegador y el cajón del carrito. Aquí el carrito es el de Shopify:
  AJAX Cart API (/cart/add.js, /cart/change.js) y Section Rendering API para repintar el cajón.

  window.Patio
    carrito.agregar(variantId, cantidad = 1, propiedades = {}, opciones = {}) → Promise<cart>
    carrito.cambiar(lineKey, cantidad) → Promise<cart>
    carrito.abrir(desde?, mensaje?) / carrito.cerrar() / carrito.refrescar() → Promise<cart>
    favoritos.lista() / favoritos.tiene(handle) / favoritos.alternar(handle) → boolean
    avisar(texto), dinero(centavos), pesos(n), revelar(contenedor?)
  Eventos en document: patio:carrito-actualizado (detail: cart), patio:favoritos-actualizados, patio:listo.
*/
(() => {
  'use strict';

  const raiz = document.documentElement;
  const tema = window.patioTema || {};
  const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const rutas = Object.assign({
    carrito: base + 'cart',
    agregar: base + 'cart/add',
    cambiar: base + 'cart/change'
  }, tema.rutas || {});
  const textos = Object.assign({
    menu: 'Menú',
    cerrar: 'Cerrar',
    carritoUna: 'Carrito, {n} pieza',
    carritoVarias: 'Carrito, {n} piezas',
    laPieza: 'la pieza',
    favGuardado: '{nombre}: guardada en tus favoritos.',
    favQuitado: '{nombre}: quitada de tus favoritos.',
    agregado: 'Agregaste a tu carrito: {nombre}.',
    agregadas: 'Agregaste {n} piezas a tu carrito.',
    quitado: '{nombre} salió de tu carrito.',
    cantidad: '{nombre}: ahora llevas {n}.',
    talla: 'talla',
    error: 'No pudimos actualizar tu carrito. Intenta de nuevo.'
  }, tema.textos || {});
  const SECCION = 'cart-drawer';
  const CLAVE_FAVS = 'patio-favoritos';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const enEditor = () => !!(window.Shopify && window.Shopify.designMode);
  const fmt = (t, v = {}) => String(t).replace(/\{(\w+)\}/g, (m, k) => (k in v ? v[k] : m));

  /* Guardado local, a prueba de navegadores que no lo dejan */
  const memoria = {};
  const leer = (k, d) => {
    try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; }
    catch (e) { return k in memoria ? memoria[k] : d; }
  };
  const guardar = (k, v) => {
    memoria[k] = v;
    try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* sin almacenamiento: queda en memoria */ }
  };

  /* Aviso para lectores de pantalla */
  const avisar = texto => {
    const el = document.getElementById('aviso');
    if (!el) return;
    el.textContent = '';
    setTimeout(() => { el.textContent = texto; }, 40);
  };

  /* Dinero: $3,450 sin centavos cuando son cero */
  const pesos = n => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const dinero = centavos => {
    const n = Number(centavos) / 100;
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
  };

  /* ============ Menú de celular: disclosure simple ============ */
  const cabecera = () => document.querySelector('.cabecera');
  const botonMenu = () => document.querySelector('.cabecera .menu-btn');
  const menuAbierto = () => { const b = botonMenu(); return !!b && b.getAttribute('aria-expanded') === 'true'; };
  const ponerMenu = abierto => {
    const cab = cabecera();
    const boton = botonMenu();
    const menu = document.getElementById('menu-movil');
    if (!cab || !boton || !menu) return;
    boton.setAttribute('aria-expanded', String(abierto));
    menu.dataset.open = String(abierto);
    cab.dataset.menu = abierto ? 'open' : 'closed';
    const txt = boton.querySelector('.menu-btn__txt');
    if (txt) txt.textContent = abierto ? textos.cerrar : textos.menu;
    if (abierto) menu.removeAttribute('inert'); else menu.setAttribute('inert', '');
  };
  document.addEventListener('click', e => {
    const b = e.target.closest('.cabecera .menu-btn');
    if (b) { ponerMenu(b.getAttribute('aria-expanded') !== 'true'); return; }
    if (e.target.closest('#menu-movil a')) ponerMenu(false);
  });
  window.matchMedia('(min-width: 1024px)').addEventListener('change', e => { if (e.matches) ponerMenu(false); });

  /* ============ La cabecera toma el color del muro que tienes enfrente ============ */
  let pendiente = false;
  const actualizarCabecera = () => {
    pendiente = false;
    const cab = cabecera();
    if (!cab) return;
    const barra = cab.querySelector('.cabecera__barra') || cab;
    const rb = barra.getBoundingClientRect();
    const sonda = rb.top + rb.height / 2;
    for (const m of document.querySelectorAll('main [data-wall], footer[data-wall]')) {
      const r = m.getBoundingClientRect();
      if (r.top <= sonda && r.bottom > sonda) {
        if (cab.dataset.wall !== m.dataset.wall) cab.dataset.wall = m.dataset.wall;
        break;
      }
    }
    cab.dataset.top = window.scrollY < 12 ? 'true' : 'false';
  };
  const programar = () => { if (!pendiente) { pendiente = true; requestAnimationFrame(actualizarCabecera); } };

  /* ============ Aparición suave (solo donde una sección pone data-reveal) ============ */
  let observador = null;
  const revelar = (contenedor = document) => {
    if (!contenedor || !contenedor.querySelectorAll) return;
    const items = [...contenedor.querySelectorAll('[data-reveal]:not(.is-in)')];
    if (contenedor.matches && contenedor.matches('[data-reveal]:not(.is-in)')) items.push(contenedor);
    if (!items.length) return;
    if (enEditor() || reduce.matches || !('IntersectionObserver' in window)) {
      items.forEach(el => el.classList.add('is-in'));
      return;
    }
    if (!observador) {
      observador = new IntersectionObserver(entradas => {
        for (const en of entradas) if (en.isIntersecting) { en.target.classList.add('is-in'); observador.unobserve(en.target); }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }
    items.forEach(el => observador.observe(el));
  };

  /* ============ Favoritos (en este navegador) ============ */
  let favs = new Set((leer(CLAVE_FAVS, []) || []).filter(h => typeof h === 'string'));
  const pintarFavs = (contenedor = document) => {
    if (!contenedor || !contenedor.querySelectorAll) return;
    contenedor.querySelectorAll('[data-fav]').forEach(b => b.setAttribute('aria-pressed', String(favs.has(b.dataset.fav))));
  };
  const emitirFavs = (handle, guardado) => {
    document.dispatchEvent(new CustomEvent('patio:favoritos-actualizados', { detail: { lista: [...favs], handle, guardado } }));
  };
  const favoritos = {
    lista: () => [...favs],
    tiene: handle => favs.has(handle),
    alternar: handle => {
      if (!handle) return false;
      const guardado = !favs.has(handle);
      if (guardado) favs.add(handle); else favs.delete(handle);
      guardar(CLAVE_FAVS, [...favs]);
      pintarFavs();
      emitirFavs(handle, guardado);
      return guardado;
    }
  };
  document.addEventListener('click', e => {
    const b = e.target.closest('button[data-fav]');
    if (!b) return;
    e.preventDefault();
    const guardado = favoritos.alternar(b.dataset.fav);
    avisar(fmt(guardado ? textos.favGuardado : textos.favQuitado, { nombre: b.dataset.nombre || textos.laPieza }));
  });
  /* Otra pestaña cambió los favoritos */
  window.addEventListener('storage', e => {
    if (e.key !== CLAVE_FAVS) return;
    favs = new Set((leer(CLAVE_FAVS, []) || []).filter(h => typeof h === 'string'));
    pintarFavs();
    emitirFavs(null, null);
  });

  /* ============ Carrito: cajón lateral, no bloquea la página ============ */
  const cajon = () => document.getElementById('carrito');
  const disparadores = () => document.querySelectorAll('[data-abre-carrito]');
  let abrioDesde = null;
  let ocupado = false;

  const pintarCuenta = n => {
    const total = Number(n) || 0;
    document.querySelectorAll('.carrito-n').forEach(s => { s.textContent = '(' + total + ')'; });
    disparadores().forEach(a => a.setAttribute('aria-label', fmt(total === 1 ? textos.carritoUna : textos.carritoVarias, { n: total })));
  };
  const avisoCajon = (texto, tipo) => {
    const c = cajon();
    const a = c && c.querySelector('.carrito__aviso');
    if (!a) return;
    a.textContent = texto || '';
    if (tipo) a.dataset.tipo = tipo; else delete a.dataset.tipo;
  };
  const enfocarTitulo = () => {
    const t = cajon() && cajon().querySelector('.carrito__titulo');
    if (t) t.focus({ preventScroll: true });
  };

  const abrir = (desde, mensaje) => {
    const c = cajon();
    if (!c) { window.location.href = rutas.carrito; return; }
    if (c.dataset.open !== 'true') abrioDesde = desde || document.activeElement;
    avisoCajon(mensaje || '');
    c.removeAttribute('inert');
    c.dataset.open = 'true';
    raiz.dataset.carrito = 'abierto';
    disparadores().forEach(a => a.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(enfocarTitulo);
  };
  const cerrar = () => {
    const c = cajon();
    if (!c || c.dataset.open !== 'true') return;
    const dentro = c.contains(document.activeElement);
    c.dataset.open = 'false';
    c.setAttribute('inert', '');
    delete raiz.dataset.carrito;
    disparadores().forEach(a => a.setAttribute('aria-expanded', 'false'));
    if (dentro && abrioDesde && document.contains(abrioDesde)) abrioDesde.focus();
  };

  /* El cajón se repinta por partes: la cabeza y el aviso se quedan, la lista y el pie cambian */
  const repintar = html => {
    const c = cajon();
    if (!c || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    c.querySelectorAll('[data-carrito-parte]').forEach(viejo => {
      const nuevo = doc.querySelector('[data-carrito-parte="' + viejo.dataset.carritoParte + '"]');
      if (nuevo) viejo.replaceWith(document.importNode(nuevo, true));
    });
  };
  const cuentaPintada = () => {
    const l = cajon() && cajon().querySelector('[data-cuenta]');
    return l ? Number(l.dataset.cuenta) || 0 : 0;
  };
  const emitir = cart => {
    document.dispatchEvent(new CustomEvent('patio:carrito-actualizado', { detail: cart }));
  };
  const ocupar = si => {
    ocupado = si;
    const l = cajon() && cajon().querySelector('.carrito__lista');
    if (l) { if (si) l.setAttribute('aria-busy', 'true'); else l.removeAttribute('aria-busy'); }
  };

  const llamar = async (url, cuerpo) => {
    const r = await fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(cuerpo)
    });
    let datos = null;
    try { datos = await r.json(); } catch (e) { datos = null; }
    if (!r.ok || !datos || (typeof datos.status === 'number' && datos.status >= 400)) {
      const error = new Error((datos && (datos.description || datos.message)) || textos.error);
      error.estado = r.status;
      error.datos = datos;
      throw error;
    }
    return datos;
  };
  const leerCarrito = async () => {
    const r = await fetch(rutas.carrito + '.js', { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(textos.error);
    return r.json();
  };

  const describir = item => {
    const partes = (item.options_with_values || [])
      .filter(o => o.value && o.value !== 'Default Title')
      .map(o => (/talla|size/i.test(o.name) ? textos.talla + ' ' + o.value : (o.value === o.value.toUpperCase() ? o.value : o.value.toLowerCase())));
    return item.product_title + (partes.length ? ', ' + partes.join(', ') : '');
  };
  const mensajeAgregado = items => (items.length === 1
    ? fmt(textos.agregado, { nombre: describir(items[0]) })
    : fmt(textos.agregadas, { n: items.reduce((s, i) => s + (i.quantity || 1), 0) }));

  const agregar = async (variantId, cantidad = 1, propiedades = {}, opciones = {}) => {
    const items = Array.isArray(variantId)
      ? variantId.map(i => ({ id: Number(i.id), quantity: Number(i.quantity || i.cantidad) || 1, properties: i.properties || i.propiedades || {} }))
      : [{ id: Number(variantId), quantity: Number(cantidad) || 1, properties: propiedades || {} }];
    const datos = await llamar(rutas.agregar, { items, sections: SECCION, sections_url: window.location.pathname });
    if (datos.sections) repintar(datos.sections[SECCION]);
    let cart;
    try { cart = await leerCarrito(); } catch (e) { cart = { item_count: cuentaPintada(), items: [] }; }
    pintarCuenta(cart.item_count);
    emitir(cart);
    const mensaje = opciones.mensaje || mensajeAgregado(datos.items || [datos]);
    if (opciones.abrir !== false) abrir(opciones.desde, mensaje);
    avisar(mensaje);
    return cart;
  };

  const cambiar = async (lineKey, cantidad) => {
    const cart = await llamar(rutas.cambiar, {
      id: String(lineKey),
      quantity: Math.max(0, Number(cantidad) || 0),
      sections: SECCION,
      sections_url: window.location.pathname
    });
    if (cart.sections) repintar(cart.sections[SECCION]);
    pintarCuenta(cart.item_count);
    emitir(cart);
    return cart;
  };

  /* Para cuando otra parte cambió el carrito por su cuenta (por ejemplo, un formulario sin JS) */
  const refrescar = async () => {
    const [html, cart] = await Promise.all([
      fetch(window.location.pathname + '?sections=' + SECCION).then(r => (r.ok ? r.json() : {})).then(s => s[SECCION]).catch(() => null),
      leerCarrito()
    ]);
    if (html) repintar(html);
    pintarCuenta(cart.item_count);
    emitir(cart);
    return cart;
  };

  /* Abrir, cerrar, quitar y cambiar cantidad desde el cajón */
  document.addEventListener('click', e => {
    const d = e.target.closest('[data-abre-carrito]');
    if (d) {
      const c = cajon();
      if (!c) return;
      e.preventDefault();
      if (c.dataset.open === 'true') cerrar(); else abrir(d);
      return;
    }
    if (e.target.closest('[data-cierra-carrito]')) { cerrar(); return; }

    const b = e.target.closest('#carrito [data-linea]');
    if (!b) return;
    e.preventDefault();
    if (ocupado || b.getAttribute('aria-disabled') === 'true') return;
    const clave = b.dataset.linea;
    const accion = b.dataset.accion;
    const n = Number(b.dataset.cantidad) || 0;
    const nombre = b.dataset.nombre || textos.laPieza;
    ocupar(true);
    avisoCajon('');
    cambiar(clave, n)
      .then(() => {
        avisar(n === 0 ? fmt(textos.quitado, { nombre }) : fmt(textos.cantidad, { nombre, n }));
        const igual = cajon() && cajon().querySelector('[data-linea="' + CSS.escape(clave) + '"][data-accion="' + accion + '"]');
        if (n > 0 && igual) igual.focus(); else enfocarTitulo();
      })
      .catch(err => { avisoCajon(err.message || textos.error, 'error'); enfocarTitulo(); })
      .finally(() => ocupar(false));
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const c = cajon();
    if (c && c.dataset.open === 'true') { cerrar(); return; }
    if (menuAbierto()) { ponerMenu(false); const b = botonMenu(); if (b) b.focus(); }
  });
  /* No es una ventana encima de la página: tocar fuera la cierra y la página sigue viva */
  document.addEventListener('pointerdown', e => {
    const c = cajon();
    if (!c || c.dataset.open !== 'true') return;
    if (c.contains(e.target) || e.target.closest('[data-abre-carrito], [data-agrega]')) return;
    cerrar();
  });

  /* ============ Editor de temas ============ */
  document.addEventListener('shopify:section:load', e => { revelar(e.target); pintarFavs(e.target); programar(); });
  document.addEventListener('shopify:section:select', e => { if (e.target.querySelector('#carrito')) abrir(); });
  document.addEventListener('shopify:section:deselect', e => { if (e.target.querySelector('#carrito')) cerrar(); });

  /* Lo que llega después (filtros, «ver más», secciones repintadas) también se sincroniza */
  let porPintar = false;
  const vigia = new MutationObserver(cambios => {
    if (porPintar) return;
    if (!cambios.some(c => c.addedNodes.length)) return;
    porPintar = true;
    requestAnimationFrame(() => { porPintar = false; pintarFavs(); revelar(); });
  });

  const iniciar = () => {
    const c = cajon();
    if (c && c.dataset.open !== 'true') c.setAttribute('inert', '');
    pintarFavs();
    revelar();
    actualizarCabecera();
    window.addEventListener('scroll', programar, { passive: true });
    window.addEventListener('resize', programar);
    if (document.body) vigia.observe(document.body, { childList: true, subtree: true });
    raiz.dataset.patio = 'listo';
    document.dispatchEvent(new CustomEvent('patio:listo'));
  };

  window.Patio = Object.assign(window.Patio || {}, {
    carrito: { agregar, cambiar, abrir, cerrar, refrescar },
    favoritos,
    avisar,
    dinero,
    pesos,
    revelar
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();
